import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import {
  fetchApiCatalog,
  testApiRequest,
  dryRunChangeSet,
  submitChangeSet,
} from './api';
import type { TestRequest, ApiChangeSet } from './types';

// React Query hooks

export function useApiCatalog() {
  const { token, selectedTenant } = useAuthStore();

  return useQuery({
    queryKey: ['apis', 'catalog', selectedTenant],
    queryFn: () => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant selected');
      }
      console.log('Fetching API catalog with token:', token ? 'present' : 'missing', 'tenant:', selectedTenant);
      return fetchApiCatalog(token, selectedTenant);
    },
    enabled: !!token && !!selectedTenant,
    staleTime: 15000, // 15 seconds
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useApiService(serviceId: string) {
  const catalogQuery = useApiCatalog();

  return {
    ...catalogQuery,
    data: catalogQuery.data?.services.find(s => s.id === serviceId),
  };
}

export function useApiRoutes(serviceId?: string) {
  const catalogQuery = useApiCatalog();

  return {
    ...catalogQuery,
    data: serviceId 
      ? catalogQuery.data?.routes.filter(r => r.serviceId === serviceId)
      : catalogQuery.data?.routes,
  };
}

export function useApiRoute(routeId: string) {
  const catalogQuery = useApiCatalog();

  return {
    ...catalogQuery,
    data: catalogQuery.data?.routes.find(r => r.id === routeId),
  };
}

export function useApiPlugins(serviceId?: string, routeId?: string) {
  const catalogQuery = useApiCatalog();

  return {
    ...catalogQuery,
    data: catalogQuery.data?.plugins.filter(p => {
      if (serviceId && p.serviceId !== serviceId) return false;
      if (routeId && p.routeId !== routeId) return false;
      return true;
    }),
  };
}

export function useTestApiRequest() {
  const { token, selectedTenant } = useAuthStore();

  return useMutation({
    mutationFn: (request: TestRequest) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication');
      }
      return testApiRequest(token, selectedTenant, request);
    },
    meta: {
      errorMessage: 'Failed to test API request',
    },
  });
}

export function useDryRunChangeSet() {
  const { token, selectedTenant } = useAuthStore();

  return useMutation({
    mutationFn: (changeSet: ApiChangeSet) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication');
      }
      return dryRunChangeSet(token, selectedTenant, changeSet);
    },
    meta: {
      errorMessage: 'Failed to validate ChangeSet',
    },
  });
}

export function useSubmitChangeSet() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (changeSet: ApiChangeSet) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication');
      }
      return submitChangeSet(token, selectedTenant, changeSet);
    },
    onSuccess: () => {
      // Invalidate catalog to refetch after ChangeSet submission
      queryClient.invalidateQueries({ queryKey: ['apis', 'catalog'] });
    },
    meta: {
      errorMessage: 'Failed to submit ChangeSet',
    },
  });
}

// Query key factories for consistent cache management
export const apiQueryKeys = {
  all: ['apis'] as const,
  catalog: (tenant: string) => ['apis', 'catalog', tenant] as const,
  service: (serviceId: string) => ['apis', 'service', serviceId] as const,
  routes: (serviceId?: string) => ['apis', 'routes', serviceId] as const,
  route: (routeId: string) => ['apis', 'route', routeId] as const,
  plugins: (serviceId?: string, routeId?: string) => ['apis', 'plugins', serviceId, routeId] as const,
  test: (requestHash: string) => ['apis', 'test', requestHash] as const,
};

// Utility hook for refetching on tenant change
export function useRefetchOnTenantChange() {
  const { selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['apis'] });
  };
}
